import { ref, computed } from 'vue'
import { useRafFn, useTimeoutFn, tryOnMounted } from '@vueuse/core'

import type { ComputedRef, Ref } from 'vue'
import type { CircleStops } from '@app/types/ui/circular-progress/interfaces'

interface ProgressStepperReturn {
  radius: ComputedRef<number>
  displayValue: Ref<number>
  circumference: ComputedRef<number>
  center: ComputedRef<number>
  dashOffset: ComputedRef<number>
  currentTitle: ComputedRef<string>
  currentText: ComputedRef<string>
}

interface ProgressStepperPayload {
  setFinished: () => void
  size: number
  stops: CircleStops[]
  speed: number
  pauseDuration: number
  stroke: number
}

export function useCircleProgress (payload: ProgressStepperPayload): ProgressStepperReturn {
  const displayValue = ref(0)
  const currentIndex = ref(0)
  const isPaused = ref(false)
  let lastTime = 0

  const currentTitle = computed(() => {
    return payload.stops[currentIndex.value]?.title || payload.stops.at(-1)?.title || ''
  })

  const currentText = computed(() => {
    return payload.stops[currentIndex.value]?.text || payload.stops.at(-1)?.text || ''
  })

  const center = computed(() => payload.size / 2)
  const radius = computed(() => (payload.size - payload.stroke) / 2)
  const circumference = computed(() => 2 * Math.PI * radius.value)

  const dashOffset = computed(() => {
    return circumference.value - (displayValue.value / 100) * circumference.value
  })

  const { pause: pauseRaf, resume: resumeRaf } = useRafFn(({ timestamp }) => {
    if (isPaused.value) return

    if (!lastTime) lastTime = timestamp
    const delta = (timestamp - lastTime) / 1000

    lastTime = timestamp

    const increment = payload.speed * delta
    displayValue.value += increment

    const target = payload.stops[currentIndex.value]?.value || 100

    if (displayValue.value >= target) {
      displayValue.value = target
      setStop()
    }
  })

  const setStop = () => {
    pauseRaf()
    isPaused.value = true

    useTimeoutFn(() => {
      currentIndex.value++

      if (currentIndex.value >= payload.stops.length) {
        payload.setFinished()
        return
      }

      isPaused.value = false
      lastTime = 0
      resumeRaf()
    }, payload.pauseDuration)
  }

  tryOnMounted(() => {
    resumeRaf()
  })

  return {
    displayValue,
    circumference,
    radius,
    center,
    dashOffset,
    currentTitle,
    currentText,
  }
}
